import { PageHeader } from "../../../components/PortfolioPerformance/page-header";
import { Panel } from "../../../components/PortfolioPerformance/panel";
import { Skeleton } from "../../../ui/skeleton";
import { TrendLineChart } from "../../../components/PortfolioPerformance/charts/trend-line-chart";
import { useIndustryBenchmark, type Change, type ElfaComparisonRow } from "../lib/api/industry-benchmark";

/** The ELFA Comparison table's columns. A figure sits next to the month it is for (Value, As of) and the figure it is measured against sits
 *  next to its month (Previous value, Compared with). The change is one column: a % for a dollar figure, points for a rate. */
const ELFA_COLUMNS: { key: keyof ElfaComparisonRow; label: string; numeric?: boolean }[] = [
  { key: "category", label: "Category" },
  { key: "metric", label: "Metric" },
  { key: "segment", label: "Segment / response" },
  { key: "value", label: "Value", numeric: true },
  { key: "asOf", label: "As of" },
  { key: "previousValue", label: "Previous value", numeric: true },
  { key: "comparedWith", label: "Compared with" },
  { key: "change", label: "Change", numeric: true },
  { key: "notes", label: "Notes" },
];

const CHANGE_COLOR = { up: "text-approve", down: "text-decline", flat: "text-muted-foreground" } as const;
const CHANGE_ARROW = { up: "▲", down: "▼", flat: "■" } as const;

/** A card's change line: "▲ 12.2% vs Sep'25 ($853.6M)". */
function ChangeLine({ trend, comparison, secondValue }: { trend: Change; comparison?: string | null; secondValue?: string | null }) {
  return (
    <p className={`mt-1 text-xs font-medium tabular-nums ${CHANGE_COLOR[trend.direction]}`}>
      {CHANGE_ARROW[trend.direction]} {trend.value}
      {comparison ? <span className="ml-1 font-normal text-muted-foreground">{comparison}</span> : null}
      {secondValue ? <span className="ml-1 font-normal text-muted-foreground">({secondValue})</span> : null}
    </p>
  );
}

export default function IndustryBenchmark() {
  const { data, isPending, isError, error } = useIndustryBenchmark();

  return (
    <div>
      <PageHeader
        title="Industry Benchmark"
        subtitle="Navitas's own growth and quality story, in the shape the industry measures itself by, live off Athena data, alongside the industry's monthly CFI figures."
        asOfPage="industry-benchmark"
      />

      {isError ? (
        <div
          role="alert"
          className="mt-8 border border-decline/50 bg-decline/10 px-4 py-3 text-sm text-foreground"
        >
          <p className="font-semibold">Could not load industry benchmark data.</p>
          <p className="mt-1 text-muted-foreground">
            {error instanceof Error ? error.message : "Unknown failure."}
          </p>
        </div>
      ) : isPending || !data ? (
        <div className="mt-8 space-y-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : (
        <>
          {/* One row of cards. Each card spans the grid's three rows (label / value / change) and hands them to its own three lines (CSS
              subgrid), so when one label or comparison wraps onto two or more lines the others keep that space too and every card's
              value and change sit on the same line across the row. */}
          <div className="mt-8 grid auto-cols-[minmax(160px,auto)] grid-flow-col grid-rows-[auto_auto_auto] gap-x-4 overflow-x-auto">
            {data.growthStats.map((g) => (
              <div
                key={g.label}
                className={
                  "row-span-3 grid gap-y-0 border border-border bg-card p-5 [grid-template-rows:subgrid] " +
                  (g.elfa ? "grid-cols-[1fr_auto] gap-x-5" : "grid-cols-1")
                }
              >
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {g.label}
                </p>
                <p className="col-start-1 mt-1 self-start text-2xl font-semibold tabular-nums text-foreground">
                  {g.value}
                </p>
                <div className="col-start-1">
                  <ChangeLine trend={g.trend} comparison={g.comparison} secondValue={g.secondValue} />
                </div>
                {/* The industry's figure for the same thing, on the right of the card, in the same three lines (label, value, change). */}
                {g.elfa ? (
                  <>
                    <p className="col-start-2 row-start-1 border-l border-border pl-5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {g.elfa.label}
                    </p>
                    <p className="col-start-2 row-start-2 mt-1 self-start border-l border-border pl-5 text-2xl font-semibold tabular-nums text-foreground">
                      {g.elfa.value}
                    </p>
                    <div className="col-start-2 row-start-3 border-l border-border pl-5">
                      <ChangeLine trend={g.elfa.trend} comparison={g.elfa.comparison} secondValue={g.elfa.secondValue} />
                    </div>
                  </>
                ) : null}
              </div>
            ))}
          </div>

          <Panel
            className="mt-6"
            title="Our Growth Story"
            description="New business volume, and how its month-over-month change compares with the industry's (ELFA)"
          >
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div>
                <p className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">New business volume</p>
                <TrendLineChart
                  data={data.newBusinessTrend}
                  series={[
                    { dataKey: "volume", name: "New Business Volume", color: "var(--amber)" },
                  ]}
                  currency
                  height={240}
                />
              </div>
              <div>
                <p className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  Month-over-month change in new business: Navitas vs ELFA
                </p>
                {data.elfaAvailable ? (
                  <TrendLineChart
                    data={data.momTrend}
                    series={[
                      { dataKey: "navitas", name: "Navitas", color: "var(--amber)", dots: true },
                      { dataKey: "elfa", name: "ELFA", color: "hsl(var(--chart-2))", dots: true },
                    ]}
                    suffix="%"
                    decimals={1}
                    height={240}
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">
                    ELFA figures are not loaded in this environment, so there is nothing to compare with yet.
                  </p>
                )}
              </div>
            </div>
          </Panel>

          <Panel
            className="mt-6"
            title="ELFA Comparison"
            description="The industry's monthly CFI release, month over month."
          >
            {!data.elfaAvailable ? (
              <p className="text-sm text-muted-foreground">
                ELFA figures are not loaded in this environment (the ELFA tables, sql/15_elfa_monthly.sql, are not there yet).
              </p>
            ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1200px] text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                    {ELFA_COLUMNS.map((col) => (
                      <th key={col.key} className={`pb-2 pr-4 align-top ${col.numeric ? "text-right" : ""}`}>
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.elfaComparison.map((row) => (
                    <tr key={row.metric} className="border-t border-border align-top">
                      {ELFA_COLUMNS.map((col) => (
                        <td
                          key={col.key}
                          className={`py-2 pr-4 ${col.numeric ? "text-right tabular-nums" : ""} ${
                            col.key === "metric" || col.key === "value" ? "font-medium text-foreground" : ""
                          } ${col.key === "notes" ? "min-w-[280px] text-muted-foreground" : ""}`}
                        >
                          {row[col.key]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}
